import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { PlayCircle, Trophy, Users, ChevronRight, Clock } from "lucide-react";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";

/**
 * HomePage — top-level landing after login. Three primary lanes:
 *  - Ny match       → /new-match          (quick match creation across any accessible GK)
 *  - Turneringar     → /tournaments        (list, create, cross-GK comparison)
 *  - Hantera profiler → /profiles          (classic goalkeeper picker + CRUD)
 */
export default function HomePage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ gks: null, tournaments: null });
  const [activeSessions, setActiveSessions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [gks, torn, act] = await Promise.all([
          api.get("/goalkeepers"),
          api.get("/tournaments", { params: { scope: "all" } }),
          api.get("/sessions/active").catch(() => ({ data: [] })),
        ]);
        setCounts({
          gks: gks.data?.length ?? 0,
          tournaments: torn.data?.length ?? 0,
        });
        setActiveSessions(act.data || []);
      } catch {
        /* ignore – page still renders */
      }
    })();
  }, []);

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Startsida" />
      <main className="max-w-[1100px] mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Vad vill du göra?
          </div>
          <h1 className="font-display font-black uppercase tracking-widest text-4xl md:text-5xl text-white">
            Hej där
          </h1>
          <div className="text-zinc-400 mt-2">
            Starta en ny match direkt, hantera dina turneringar eller öppna en målvaktsprofil.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <HomeCard
            testid="home-new-match"
            onClick={() => navigate("/new-match")}
            color="#34C759"
            icon={<PlayCircle size={28} className="text-[#34C759]" />}
            title="Ny match"
            subtitle="Välj deltagande målvakter, turnering och motståndare – börja logga skott direkt."
            counter={null}
          />
          <HomeCard
            testid="home-tournaments"
            onClick={() => navigate("/tournaments")}
            color="#FFD60A"
            icon={<Trophy size={28} className="text-[#FFD60A]" />}
            title="Turneringar / Serier"
            subtitle="Skapa säsonger, jämför målvakter över matcher och utvärdera över tid."
            counter={counts.tournaments != null ? `${counts.tournaments} totalt` : null}
          />
          <HomeCard
            testid="home-profiles"
            onClick={() => navigate("/profiles")}
            color="#ffffff"
            icon={<Users size={28} className="text-white" />}
            title="Hantera profiler"
            subtitle="Öppna en målvakt för utvärdering, dagbok, delning eller ändra inställningar."
            counter={counts.gks != null ? `${counts.gks} profiler` : null}
          />
        </div>

        {activeSessions.length > 0 && (
          <section className="mt-10" data-testid="home-active-sessions">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-[#FF9F0A]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF9F0A] font-display">
                Pågående matcher
              </span>
            </div>
            <div className="space-y-2">
              {activeSessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`home-resume-${s.id}`}
                  onClick={() =>
                    navigate(`/gk/${s.goalkeeper_id}/session/${s.id}/register`)
                  }
                  className="w-full text-left flex items-center gap-4 border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 hover:bg-[#FF9F0A]/10 transition-colors p-4"
                >
                  <div className="w-10 h-10 border border-[#FF9F0A] flex items-center justify-center bg-black flex-shrink-0">
                    <PlayCircle size={18} className="text-[#FF9F0A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase tracking-widest text-white truncate">
                      {s.name}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                      {s.goalkeeper_name || "—"}
                      {s.opponent ? ` · vs ${s.opponent}` : ""}
                      {s.date ? ` · ${safeDate(s.date)}` : ""}
                    </div>
                  </div>
                  <span className="text-[10px] font-display uppercase tracking-widest text-[#FF9F0A] hidden sm:inline">
                    Fortsätt
                  </span>
                  <ChevronRight size={16} className="text-[#FF9F0A]" />
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function safeDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return format(d, "d MMM", { locale: sv });
  } catch {
    return "";
  }
}

function HomeCard({ testid, onClick, icon, title, subtitle, color, counter }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="card-tactical p-6 text-left h-full flex flex-col transition-colors hover:border-[#3F3F46] group"
      style={{ borderTop: `3px solid ${color}` }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
          {icon}
        </div>
      </div>
      <div className="font-display font-black uppercase tracking-widest text-2xl text-white leading-tight mb-2">
        {title}
      </div>
      <div className="text-sm text-zinc-400 flex-1">{subtitle}</div>
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#27272A]">
        <span className="text-[11px] uppercase tracking-widest font-display text-zinc-500">
          {counter || "Öppna"}
        </span>
        <ChevronRight
          size={18}
          className="text-zinc-500 group-hover:text-white transition-colors"
        />
      </div>
    </button>
  );
}
